import { ComponentType, useState, useCallback, useEffect, useRef } from 'react';
import { FixedSizeListProps, FixedSizeList as _FixedSizeList_ } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';
import { ThumbsUp, ThumbsDown, AlertCircle, Copy, Keyboard } from 'lucide-react';
import { format } from 'date-fns';
import { useQuery } from '@apollo/client';
import { toast } from 'sonner';
import { AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { ADMIN_CHATS_QUERY } from '../../graphql/queries';
import Filters from './Filters';
import EvalModal from './EvalModal';

const List = _FixedSizeList_ as unknown as ComponentType<FixedSizeListProps>;

interface FilterState {
  search: string;
  emailSearch: string;
  good?: boolean | null;
  evaluated?: boolean | null;
  from?: string;
  to?: string;
}

interface Chat {
  id: string;
  title: string;
  type: string;
  createdAt: string;
  good?: boolean | null;
  hasFeedback?: string | null;
  user?: {
    email?: string;
    numImages?: number;
    numChats?: number;
    hasSettingsInstructions?: boolean;
    isOldAsfMember?: boolean;
  };
}

interface ChatTableState {
  filters: FilterState;
  selectedIndex: number | null;
  selectedChatId: string | null;
}

interface ChatTableProps {
  onChatSelect?: (chatId: string) => void;
  className?: string;
}

function EvaluationBadge({ good }: { good?: boolean | null }) {
  const { t } = useTranslation('chat');

  if (good === true) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
        <ThumbsUp className="w-3 h-3" />
        {t('table.good')}
      </span>
    );
  } else if (good === false) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
        <ThumbsDown className="w-3 h-3" />
        {t('table.bad')}
      </span>
    );
  } else {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
        <AlertCircle className="w-3 h-3" />
        {t('table.unevaluated')}
      </span>
    );
  }
}

function FeedbackIndicator({ hasFeedback }: { hasFeedback?: string | null }) {
  if (hasFeedback === 'negative') {
    return (
      <div className="flex justify-center">
        <ThumbsDown className="w-4 h-4 text-red-600" />
      </div>
    );
  }

  if (hasFeedback === 'positive') {
    return (
      <div className="flex justify-center">
        <ThumbsUp className="w-4 h-4 text-green-600" />
      </div>
    );
  }

  // No feedback - show nothing
  return <div className="flex justify-center"></div>;
}

function KeyboardShortcuts() {
  const { t } = useTranslation('chat');

  return (
    <div className="flex items-center gap-4 text-sm">
      <div className="flex items-center gap-2 text-gray-600">
        <Keyboard className="w-4 h-4" />
        <span className="font-medium">{t('table.shortcuts')}</span>
      </div>
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-1">
          <kbd className="px-2 py-1 bg-gray-100 border border-gray-300 rounded text-xs font-mono">↑↓</kbd>
          <span className="text-gray-600">{t('table.navigate')}</span>
        </div>
        <div className="flex items-center gap-1">
          <kbd className="px-2 py-1 bg-gray-100 border border-gray-300 rounded text-xs font-mono">Enter</kbd>
          <span className="text-gray-600">{t('table.open')}</span>
        </div>
      </div>
    </div>
  );
}

function ChatTable({ onChatSelect, className = '' }: ChatTableProps) {
  const { t } = useTranslation('chat');
  const { t: tc } = useTranslation('common');
  const [state, setState] = useState<ChatTableState>({
    filters: { search: '', emailSearch: '' },
    selectedIndex: null,
    selectedChatId: null,
  });

  const [loadingMore, setLoadingMore] = useState(false);
  const limit = 50;
  const tableContainerRef = useRef<HTMLDivElement>(null);

  // Build query variables from filters
  const getQueryVariables = useCallback(() => {
    const variables: any = {
      limit,
      offset: 0,
    };

    // Add filters
    if (state.filters.good !== undefined) variables.good = state.filters.good;
    if (state.filters.evaluated !== undefined) variables.evaluated = state.filters.evaluated;
    if (state.filters.from) variables.from = new Date(state.filters.from);
    if (state.filters.to) variables.to = new Date(state.filters.to);
    if (state.filters.emailSearch?.trim()) variables.email = state.filters.emailSearch.trim();
    if (state.filters.search?.trim()) variables.chatId = state.filters.search.trim();

    return variables;
  }, [state.filters, limit]);

  const { data, loading, fetchMore, refetch } = useQuery(ADMIN_CHATS_QUERY, {
    variables: getQueryVariables(),
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const allChats = (data?.adminChats?.chats || []) as Chat[];
  const totalChats = data?.adminChats?.total || 0;
  const hasMoreData = allChats.length < totalChats && totalChats > 0;

  const loadMoreChats = useCallback(async () => {
    if (!hasMoreData || loading || loadingMore || allChats.length >= totalChats) return;

    setLoadingMore(true);
    try {
      await fetchMore({
        variables: {
          ...getQueryVariables(),
          offset: allChats.length,
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult?.adminChats?.chats?.length) return prev;

          const existingIds = new Set(prev.adminChats.chats.map((chat: any) => chat.id));
          const newChats = fetchMoreResult.adminChats.chats.filter((chat: any) => !existingIds.has(chat.id));

          if (newChats.length === 0) return prev;

          return {
            adminChats: {
              ...prev.adminChats,
              chats: [...prev.adminChats.chats, ...newChats],
              total: Math.max(prev.adminChats.total, fetchMoreResult.adminChats.total),
            },
          };
        },
      });
    } catch (error) {
      console.error('Error loading more chats:', error);
    } finally {
      setLoadingMore(false);
    }
  }, [fetchMore, allChats.length, hasMoreData, loading, loadingMore, totalChats, getQueryVariables]);

  // Debounce search
  useEffect(() => {
    const timeoutId = setTimeout(async () => {
      await refetch(getQueryVariables());
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [refetch, getQueryVariables]);

  const filteredChats: Chat[] = allChats;

  const updateFilter = useCallback((key: string, value: any) => {
    setState((prev) => ({
      ...prev,
      filters: { ...prev.filters, [key]: value },
      selectedIndex: null,
    }));
  }, []);

  const clearFilters = useCallback(() => {
    setState((prev) => ({
      ...prev,
      filters: { search: '', emailSearch: '' },
      selectedIndex: null,
    }));
  }, []);

  const selectChat = useCallback(
    (index: number, chatId: string) => {
      setState((prev) => ({ ...prev, selectedIndex: index, selectedChatId: chatId }));
      onChatSelect?.(chatId);
    },
    [onChatSelect]
  );

  const closeModal = useCallback(() => {
    setState((prev) => ({ ...prev, selectedChatId: null }));
  }, []);

  // Keyboard navigation
  useEffect(() => {
    if (filteredChats.length === 0) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (state.selectedChatId) {
        if (e.key === 'Escape') {
          e.preventDefault();
          closeModal();
        }
        return;
      }

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setState((prev) => ({
          ...prev,
          selectedIndex: prev.selectedIndex === null ? 0 : Math.min(prev.selectedIndex + 1, filteredChats.length - 1),
        }));
      }

      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setState((prev) => ({
          ...prev,
          selectedIndex: prev.selectedIndex === null ? 0 : Math.max(prev.selectedIndex - 1, 0),
        }));
      }

      if ((e.key === 'Enter' || e.key === ' ') && state.selectedIndex !== null) {
        e.preventDefault();
        const selectedChat = filteredChats[state.selectedIndex];
        setState((prev) => ({ ...prev, selectedChatId: selectedChat.id }));
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [filteredChats, state.selectedIndex, state.selectedChatId, closeModal]);

  const ChatRow = ({ index, style }: { index: number; style: React.CSSProperties }) => {
    const chat: Chat = filteredChats[index];
    const isSelected = state.selectedIndex === index;

    // Load more data when approaching the end of the list
    useEffect(() => {
      if (index >= allChats.length - 5 && hasMoreData && !loadingMore) {
        loadMoreChats();
      }
    }, [index, allChats.length, hasMoreData, loadMoreChats, loadingMore]);

    return (
      <div
        style={style}
        className={`border-b cursor-pointer hover:bg-blue-50 ${isSelected ? 'bg-blue-50' : ''} h-[40px] flex items-center`}
        onClick={() => selectChat(index, chat.id)}
      >
        <div className="grid grid-cols-[100px_120px_80px_200px_150px_80px_80px_80px_100px_120px_40px] items-center px-4 text-xs w-full">
          <div className="flex items-center">{format(new Date(chat.createdAt), 'M/d h:mm a')}</div>
          <div className="flex items-center gap-1 pr-4">
            <Link
              to={`/admin/chats/${chat.id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="truncate text-blue-600 hover:text-blue-800 hover:underline"
              title={chat.id}
              onClick={(e) => e.stopPropagation()}
            >
              {chat.id.substring(0, 8)}...
            </Link>
            <button
              onClick={(e) => {
                e.stopPropagation();
                navigator.clipboard.writeText(chat.id);
                toast.success(t('table.id-copied'));
              }}
              className="text-zinc-500 hover:text-zinc-700"
            >
              <Copy className="h-4 w-4" />
            </button>
          </div>
          <div className="flex items-center">{chat.type}</div>
          <div className="flex items-center gap-1 pr-3">
            <div className="truncate" title={chat.user?.email || t('table.no-email')}>
              {chat.user?.email || t('table.no-email')}
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                navigator.clipboard.writeText(chat.user?.email || '');
                toast.success(t('table.email-copied'));
              }}
              className="text-zinc-500 hover:text-zinc-700"
            >
              <Copy className="h-4 w-4" />
            </button>
          </div>
          <div className="flex items-center truncate">{chat.title}</div>
          <div className="flex items-center justify-center">{chat.user?.numImages || 0}</div>
          <div className="flex items-center justify-center">{chat.user?.numChats || 0}</div>
          <div className="flex items-center justify-center">
            {chat.user?.hasSettingsInstructions ? tc('yes') : tc('no')}
          </div>
          <div className="flex items-center justify-center">{chat.user?.isOldAsfMember ? tc('yes') : tc('no')}</div>
          <div className="flex items-center">
            <EvaluationBadge good={chat.good} />
          </div>
          <div className="flex items-center">
            <FeedbackIndicator hasFeedback={chat.hasFeedback} />
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={className}>
      <Filters state={state.filters} onUpdate={updateFilter} onClear={clearFilters} />

      <div className="flex justify-between items-center mb-4">
        <div className="text-sm text-gray-600">
          {loading ? tc('loading') : t('table.chat-count', { shown: filteredChats.length, total: totalChats })}
        </div>
        <KeyboardShortcuts />
      </div>

      <div className="rounded-md border bg-white overflow-hidden">
        <div className="bg-zinc-50 border-b h-[40px] flex items-center">
          <div className="grid grid-cols-[100px_120px_80px_200px_150px_80px_80px_80px_100px_120px_40px] items-center px-4 text-xs font-medium w-full">
            <div>{t('table.col-date')}</div>
            <div>{t('table.col-chat-id')}</div>
            <div>{t('table.col-type')}</div>
            <div>{t('table.col-email')}</div>
            <div>{t('table.col-title')}</div>
            <div className="text-center">{t('table.col-images')}</div>
            <div className="text-center">{t('table.col-chats')}</div>
            <div className="text-center">{t('table.col-instructions')}</div>
            <div className="text-center">{t('table.col-asf-member')}</div>
            <div>{t('table.col-evaluation')}</div>
            <div></div>
          </div>
        </div>
        <div ref={tableContainerRef} style={{ height: 'calc(100dvh - 500px)' }} className="overflow-y-auto">
          <AutoSizer>
            {({ height, width }) => (
              <List height={height} itemCount={filteredChats.length} itemSize={40} width={width}>
                {ChatRow}
              </List>
            )}
          </AutoSizer>
        </div>
      </div>

      <AnimatePresence>
        {state.selectedChatId && <EvalModal chatId={state.selectedChatId} onClose={closeModal} />}
      </AnimatePresence>
    </div>
  );
}

export default ChatTable;
